import * as React from "react";

import { getNumericEnv } from "@/env";
import Debug from "@/react/debug";
import { createNotifier, errorNotification } from "@/react/notifications";
import {
  handleMutationResponse as createMutationResponseHandler,
  useGetTenantAudioFilesQuery,
  createBulkMutationResponseHandler,
} from "@/react/services/api";
import { KEY } from "@/react/components/table/useTableMutations";

import {
  useCreateInboundQueuesMutation,
  useBulkCreateInboundQueuesMutation,
  useDeleteQueuesMutation,
  useUpdateInboundQueuesMutation,
} from "@/react/features/vcc/common/api/queues";
import transformInboundQueuesData from "./transformInboundQueuesData";
import { withAudioFileId } from "./audioFileTransforms";
import schema from "./schema";

const createErrorHandler = (message) => {
  return (error) => {
    console.error(`${message}:`, error);
    errorNotification(error);
  };
};

const ENTITY_NAME = "inbound phone queue";

const PRIMARY_KEY = schema.fields.id.spec.key;

/**
 * "name" is also defined as constant - to be used as an error fallback.
 * Given that the ID column is hidden on this table, "name" will serve as a
 * catch-all/fallback for all row-level errors. Look for "fallbackKey" logic
 * throughout {@link createMutationResponseHandler}
 * and {@link createBulkMutationResponseHandler}.
 */
const NAME_KEY = schema.fields.name.spec.key;
/**
 * Mutating more than this number of resources will switch from the normal
 * flow to a bulk mutation flow, where the response from the API will be
 * the ID of an async job.
 * @type {number | null}
 */
const BULK_JOB_THRESHOLD = getNumericEnv("VUE_APP_BULK_JOB_THRESHOLD");

const useInboundQueuesMutations = (tenantName, mutations) => {
  const {
    rowsToCreate,
    setRowsToCreate,
    rowsToUpdate,
    setRowsToUpdate,
    rowsToDelete,
    setRowsToDelete,
    setData,
    resetData,
    addRows,
  } = mutations;
  const [createInboundQueues, { isLoading: isCreating }] =
    useCreateInboundQueuesMutation();
  const [updateInboundQueues, { isLoading: isUpdating }] =
    useUpdateInboundQueuesMutation();
  const [deleteQueues, { isLoading: isDeleting }] = useDeleteQueuesMutation();
  const { data: audioFiles } = useGetTenantAudioFilesQuery({
    tenantName,
  });
  const [bulkCreateInboundQueues, { isLoading: isCreatingBulk }] =
    useBulkCreateInboundQueuesMutation();

  const onCreateResponseHandler = React.useMemo(
    () =>
      createMutationResponseHandler({
        rows: rowsToCreate,
        setRows: setRowsToCreate,
        setData,
        responseMappingKey: PRIMARY_KEY,
        idKey: KEY,
        customFallbackKey: NAME_KEY,
        notifyContent: createNotifier("create", ENTITY_NAME),
        isVoidResponse: false,
      }),
    [rowsToCreate, setRowsToCreate, setData]
  );
  const onUpdateResponseHandler = React.useMemo(
    () =>
      createMutationResponseHandler({
        rows: rowsToUpdate,
        setRows: setRowsToUpdate,
        setData,
        responseMappingKey: PRIMARY_KEY,
        idKey: KEY,
        customFallbackKey: NAME_KEY,
        notifyContent: createNotifier("update", ENTITY_NAME),
        isVoidResponse: false,
      }),
    [rowsToUpdate, setRowsToUpdate, setData]
  );
  const onDeleteResponseHandler = React.useMemo(
    () =>
      createMutationResponseHandler({
        rows: rowsToDelete,
        setRows: setRowsToDelete,
        setData,
        responseMappingKey: PRIMARY_KEY,
        idKey: KEY,
        customFallbackKey: NAME_KEY,
        notifyContent: createNotifier("delete", ENTITY_NAME),
        isVoidResponse: true,
      }),
    [rowsToDelete, setRowsToDelete, setData]
  );

  const onSave = React.useCallback(() => {
    if (rowsToCreate.size > 0) {
      // TODO: Merge all transformations into one function
      const inboundQueues = withAudioFileId({
        audioFiles,
        inboundQueues: transformInboundQueuesData(rowsToCreate, true),
      });

      if (rowsToCreate.size >= BULK_JOB_THRESHOLD) {
        Debug.info(
          "[useInboundQueuesMutation] Using bulk job endpoint for" +
            `${rowsToCreate.size} rows (threshold: ${BULK_JOB_THRESHOLD}).`
        );

        bulkCreateInboundQueues({
          tenantName,
          inboundQueues,
        })
          .unwrap()
          .then(
            createBulkMutationResponseHandler({
              currentRows: rowsToCreate,
              setRows: setRowsToCreate,
              setData,
              idKey: PRIMARY_KEY,
              responseMappingKey: PRIMARY_KEY,
              customFallbackKey: NAME_KEY,
              options: {
                buildMessage: (uuid) =>
                  `Bulk Creation Job for Queues has started, with ID ${uuid}.`,
              },
            })
          )
          .catch(createErrorHandler("Failed to create inbound queues in bulk"));
      } else {
        createInboundQueues({
          tenantName,
          inboundQueues,
        })
          .unwrap()
          .then(onCreateResponseHandler)
          .catch(createErrorHandler("Failed to create inbound queues"));
      }
    }

    if (rowsToUpdate.size > 0) {
      const inboundQueues = withAudioFileId({
        audioFiles,
        inboundQueues: transformInboundQueuesData(rowsToUpdate),
      });

      updateInboundQueues({
        tenantName,
        inboundQueues,
      })
        .unwrap()
        .then(onUpdateResponseHandler)
        .catch(createErrorHandler("Failed to update inbound queues"));
    }

    if (rowsToDelete.size > 0) {
      const queueIds = Array.from(rowsToDelete.keys());
      deleteQueues({ tenantName, queueIds })
        .unwrap()
        .then(onDeleteResponseHandler)
        .catch(createErrorHandler("Failed to delete inbound queues"));
    }
  }, [
    rowsToCreate,
    rowsToUpdate,
    rowsToDelete,
    createInboundQueues,
    updateInboundQueues,
    deleteQueues,
    onCreateResponseHandler,
    onUpdateResponseHandler,
    onDeleteResponseHandler,
  ]);

  // TODO: Fix the underlying issues as part of TAP-2806:
  //  - addRows' default implementation does now know what the primary key is just from the Yup schema
  //  - the _key attribute is indeed set but we don't move it into the primary key
  //  - TAP could crash if it deserializes an object sent with a "_key" attribute
  const addRowsWithUuid = React.useCallback(
    (rowsToAdd, _schema) => {
      const rowsWithUuid = rowsToAdd.map((row) => {
        const key = crypto.randomUUID();
        return {
          ...row,
          [KEY]: key,
          [_schema.fields.id.spec.key]: key,
        };
      });

      addRows(rowsWithUuid, _schema);
    },
    [addRows]
  );

  return {
    onSave,
    onCancel: resetData,
    isMutationOngoing: isUpdating || isCreating || isDeleting || isCreatingBulk,
    filteredMutations: {
      ...mutations,
      addRows: addRowsWithUuid,
    },
    isDirty:
      rowsToCreate.size > 0 || rowsToUpdate.size > 0 || rowsToDelete.size > 0,
  };
};

export default useInboundQueuesMutations;
